import React, { Component } from "react";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  useMap
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Select, message } from "antd";
import Fetch from "../../utils/fetch";
import "./index.css";

const { Option } = Select;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

function FitBounds({ positions }) {
  const map = useMap();
  React.useEffect(() => {
    if (positions && positions.length > 0) {
      map.fitBounds(positions, { padding: [40, 40] });
    }
  }, [positions, map]);
  return null;
}

function InvalidateSize({ isFullscreen }) {
  const map = useMap();
  React.useEffect(() => {
    setTimeout(() => map.invalidateSize(), 50);
  }, [isFullscreen, map]);
  return null;
}

function TripMap({
  color,
  route,
  roadRoute,
  gpsPoints,
  label,
  isFullscreen,
  onToggleFullscreen
}) {
  const stationPositions = route.map(s => [s.lat, s.lng]);
  const polylinePositions = roadRoute.length > 0 ? roadRoute : stationPositions;
  const gpsPolyline =
    gpsPoints && gpsPoints.length > 1 ? gpsPoints.map(p => [p.lat, p.lng]) : [];

  return (
    <div className={`rm-map-half${isFullscreen ? " rm-map-fullscreen" : ""}`}>
      <div className="rm-map-label" style={{ borderColor: color }}>
        {label}
      </div>
      <button
        className="rm-fullscreen-btn"
        onClick={onToggleFullscreen}
        title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
      >
        {isFullscreen ? "⛶" : "⛶"}
        {isFullscreen ? " Exit" : " Fullscreen"}
      </button>
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        minZoom={4}
        maxZoom={18}
        maxBounds={[[6.4, 68.1], [37.6, 97.4]]}
        maxBoundsViscosity={1.0}
        className="rm-leaflet-map"
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {gpsPolyline.length > 1 && (
          <Polyline
            positions={gpsPolyline}
            color="#e63946"
            weight={3}
            dashArray="6,4"
          />
        )}

        {polylinePositions.length > 1 && (
          <Polyline positions={polylinePositions} color={color} weight={4} />
        )}

        {route.map((station, i) => (
          <Marker key={station.id || i} position={[station.lat, station.lng]}>
            <Popup>
              <div className="rm-popup">
                <div className="rm-popup-name">{station.name}</div>
                {station.address && (
                  <div className="rm-popup-row">
                    <span>Address:</span> {station.address}
                  </div>
                )}
                {station.type && (
                  <div className="rm-popup-row">
                    <span>Type:</span> {station.type}
                  </div>
                )}
                {station.status && (
                  <div className="rm-popup-row">
                    <span>Status:</span> {station.status}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {stationPositions.length > 0 && (
          <FitBounds positions={stationPositions} />
        )}
        <InvalidateSize isFullscreen={isFullscreen} />
      </MapContainer>
    </div>
  );
}

const STATUS_OPTIONS = ["ONTRIP", "COMPLETED", "ACTIVE", "CANCELLED"];
const SEARCH_MODES = [
  { key: "dateStatus", label: "Date & Status" },
  { key: "tripId", label: "Trip ID" },
  { key: "dateAndBusRc", label: "Date & Bus RC" }
];

function today() {
  return new Date().toISOString().split("T")[0];
}

class TripPanel extends Component {
  render() {
    const {
      type,
      number,
      title,
      subtitle,
      date,
      minDate,
      maxDate,
      status,
      trips,
      searched,
      selectedTripId,
      tripDetail,
      onDateChange,
      onStatusChange,
      onSearch,
      onTripSelect,
      searchMode,
      onSearchModeChange,
      tripIdInput,
      onTripIdInputChange,
      busRc,
      onBusRcChange,
      isMapped,
      mappedExistingTripId
    } = this.props;

    const isPrimary = searchMode !== undefined;

    return (
      <div className={`rm-panel ${type}`}>
        <div className="rm-panel-header">
          <div className="rm-circle">{number}</div>
          <div className="rm-header-text">
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          {(tripDetail || isMapped) && (
            <div className="rm-header-badges">
              {isMapped ? (
                <span className="rm-hbadge rm-hbadge-mapped">
                  ✓ MAPPED
                  {mappedExistingTripId && (
                    <span className="rm-hbadge-mapped-id">
                      {mappedExistingTripId}
                    </span>
                  )}
                </span>
              ) : isPrimary && tripDetail ? (
                <span className="rm-hbadge rm-hbadge-unmapped">NOT MAPPED</span>
              ) : null}
              {!isMapped && tripDetail && tripDetail.tripId && !isPrimary && (
                <span className="rm-hbadge rm-hbadge-id" title="Trip ID">
                  {tripDetail.tripId}
                </span>
              )}
              {tripDetail && tripDetail.busRc && (
                <span className="rm-hbadge rm-hbadge-rc">
                  {tripDetail.busRc}
                </span>
              )}
            </div>
          )}
        </div>

        {isPrimary && (
          <div className="rm-search-modes">
            {SEARCH_MODES.map(m => (
              <button
                key={m.key}
                className={`rm-mode-tab${
                  searchMode === m.key ? " active" : ""
                }`}
                onClick={() => onSearchModeChange(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        <div className="rm-controls-row">
          {(!isPrimary || searchMode === "dateStatus") && (
            <>
              <div className="rm-filter-group">
                <label>Date</label>
                <input
                  type="date"
                  value={date}
                  min={minDate}
                  max={maxDate}
                  onChange={e => onDateChange(e.target.value)}
                />
              </div>
              <div className="rm-filter-group">
                <label>Status</label>
                <select
                  value={status}
                  onChange={e => onStatusChange(e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {searchMode === "tripId" && (
            <div className="rm-filter-group rm-trip-select-group">
              <label>Trip ID</label>
              <input
                type="text"
                className="rm-text-input"
                value={tripIdInput}
                onChange={e => onTripIdInputChange(e.target.value)}
                placeholder="Enter Trip ID"
                onKeyDown={e => e.key === "Enter" && onSearch()}
              />
            </div>
          )}

          {searchMode === "dateAndBusRc" && (
            <>
              <div className="rm-filter-group">
                <label>Date</label>
                <input
                  type="date"
                  value={date}
                  min={minDate}
                  max={maxDate}
                  onChange={e => onDateChange(e.target.value)}
                />
              </div>
              <div className="rm-filter-group">
                <label>Bus RC</label>
                <input
                  type="text"
                  className="rm-text-input"
                  value={busRc}
                  onChange={e => onBusRcChange(e.target.value)}
                  placeholder="Enter Bus RC"
                  onKeyDown={e => e.key === "Enter" && onSearch()}
                />
              </div>
            </>
          )}

          <div className="rm-filter-group rm-search-group">
            <label>&nbsp;</label>
            <button className="rm-search-btn" onClick={onSearch}>
              ● Search
            </button>
          </div>
          {searched && (
            <div className="rm-filter-group rm-trip-select-group">
              <label>Choose Trip</label>
              <Select
                showSearch
                value={selectedTripId || undefined}
                placeholder={
                  trips.length === 0 ? "No trips found" : "Select a trip"
                }
                disabled={trips.length === 0}
                onChange={v => onTripSelect(v)}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                style={{ width: "100%" }}
                dropdownMatchSelectWidth={false}
              >
                {trips.map(t => (
                  <Option key={t._id} value={t._id}>
                    {t.service ? t.service.name : t._id}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  }
}

class RouteMapping extends Component {
  constructor(props) {
    super(props);
    this.state = {
      primaryDate: today(),
      primaryStatus: "ONTRIP",
      primarySearchMode: "dateStatus",
      primaryTripIdInput: "",
      primaryBusRc: "",
      primarySearched: false,
      primaryLoading: false,
      primaryTrips: [],
      primaryTripId: "",
      primaryDetail: null,
      primaryRoute: [],
      primaryRoadRoute: [],
      primaryGpsPoints: [],
      primaryServiceId: "",
      primaryIsMapped: false,
      primaryMappedTripId: "",

      mappedDate: today(),
      mappedStatus: "COMPLETED",
      mappedSearchMode: "dateStatus",
      mappedTripIdInput: "",
      mappedBusRc: "",
      mappedSearched: false,
      mappedLoading: false,
      mappedTrips: [],
      mappedTripId: "",
      mappedDetail: null,
      mappedRoute: [],
      mappedRoadRoute: [],
      mappedGpsPoints: [],
      isMapped: false,
      fullscreenMap: null
    };
  }

  async handleSearch(side) {
    if (side === "primary") {
      const {
        primaryDate,
        primaryStatus,
        primarySearchMode,
        primaryTripIdInput,
        primaryBusRc
      } = this.state;
      this.setState({
        primarySearched: false,
        primaryTrips: [],
        primaryTripId: "",
        primaryRoute: [],
        primaryRoadRoute: [],
        primaryGpsPoints: [],
        primaryIsMapped: false,
        primaryMappedTripId: ""
      });

      let searchData;
      if (primarySearchMode === "tripId") {
        searchData = { tripId: primaryTripIdInput };
      } else if (primarySearchMode === "dateAndBusRc") {
        searchData = { date: primaryDate, busRc: primaryBusRc };
      } else {
        searchData = { date: primaryDate, status: primaryStatus };
      }

      const data = await Fetch("/trip-mapping/fetchTrips", {
        method: "post",
        data: searchData
      });

      if (primarySearchMode === "tripId") {
        if (!data || data.length === 0) {
          message.warning("No trip found for this Trip ID");
          return;
        }
        this.setState({ primaryTrips: data }, () => {
          this.handleTripSelect("primary", data[0]._id);
        });
      } else {
        this.setState({ primarySearched: true, primaryTrips: data || [] });
      }
    } else {
      const {
        mappedDate,
        mappedStatus,
        mappedSearchMode,
        mappedTripIdInput,
        mappedBusRc
      } = this.state;
      this.setState({
        mappedSearched: false,
        mappedTrips: [],
        mappedTripId: "",
        mappedRoute: [],
        mappedRoadRoute: [],
        mappedGpsPoints: [],
        isMapped: false
      });

      let searchData;
      if (mappedSearchMode === "tripId") {
        searchData = { tripId: mappedTripIdInput };
      } else if (mappedSearchMode === "dateAndBusRc") {
        searchData = { date: mappedDate, busRc: mappedBusRc };
      } else {
        searchData = { date: mappedDate, status: mappedStatus };
      }

      const data = await Fetch("/trip-mapping/fetchTrips", {
        method: "post",
        data: searchData
      });

      if (mappedSearchMode === "tripId") {
        if (!data || data.length === 0) {
          message.warning("No trip found for this Trip ID");
          return;
        }
        this.setState({ mappedTrips: data }, () => {
          this.handleTripSelect("mapped", data[0]._id);
        });
      } else {
        this.setState({ mappedSearched: true, mappedTrips: data || [] });
      }
    }
  }

  async handleTripSelect(side, tripId) {
    const tripsKey = side === "primary" ? "primaryTrips" : "mappedTrips";
    const trips = this.state[tripsKey];
    const trip = trips.find(t => t._id === tripId);

    if (!trip) {
      side === "primary"
        ? this.setState({
            primaryTripId: tripId,
            primaryRoute: [],
            primaryRoadRoute: [],
            primaryGpsPoints: [],
            primaryDetail: null,
            primaryIsMapped: false,
            primaryMappedTripId: ""
          })
        : this.setState({
            mappedTripId: tripId,
            mappedRoute: [],
            mappedRoadRoute: [],
            mappedDetail: null
          });
      return;
    }

    const route = (trip.route || [])
      .filter(r => r.station && r.station.lat && r.station.lng)
      .sort((a, b) => (a.millisFromStart || 0) - (b.millisFromStart || 0))
      .map(r => ({
        lat: parseFloat(r.station.lat),
        lng: parseFloat(r.station.lng),
        name: r.station.name,
        address: r.station.address,
        type: r.station.type,
        status: r.station.status,
        id: r.station._id
      }));

    const detail = {
      service: trip.service ? trip.service.name : "",
      tripId: trip._id,
      busRc: trip.bus ? trip.bus.rc : "",
      status: trip.status || ""
    };

    if (side === "primary") {
      this.setState({
        primaryTripId: tripId,
        primaryDetail: detail,
        primaryRoute: [],
        primaryRoadRoute: [],
        primaryGpsPoints: [],
        primaryLoading: true
      });

      const roadFetch =
        route.length >= 2
          ? Fetch("/trip-mapping/fetchRoute", {
              method: "post",
              data: { waypoints: route.map(s => ({ lat: s.lat, lng: s.lng })) }
            })
          : Promise.resolve(null);
      const gpsFetch = Fetch("/trip-mapping/fetchMappedTripsFromMongo", {
        method: "post",
        data: { side: "primary", serviceId: trip.serviceId }
      });

      const [roadData, gpsData] = await Promise.all([roadFetch, gpsFetch]);

      const primaryIsMapped = (gpsData && gpsData.isMapped) || false;
      const primaryMappedTripId = (gpsData && gpsData.mappedTripId) || "";

      this.setState({
        primaryRoute: route,
        primaryServiceId: trip.serviceId || "",
        primaryRoadRoute:
          roadData && roadData.length > 0
            ? roadData.map(p => [p.lat, p.lng])
            : [],
        primaryGpsPoints:
          gpsData && gpsData.gpsPoints && gpsData.gpsPoints.length > 0
            ? gpsData.gpsPoints
            : [],
        primaryIsMapped,
        primaryMappedTripId,
        primaryLoading: false
      });
    } else {
      this.setState({
        mappedTripId: tripId,
        mappedDetail: detail,
        mappedRoute: [],
        mappedRoadRoute: [],
        mappedGpsPoints: [],
        mappedLoading: true
      });

      const roadFetch =
        route.length >= 2
          ? Fetch("/trip-mapping/fetchRoute", {
              method: "post",
              data: { waypoints: route.map(s => ({ lat: s.lat, lng: s.lng })) }
            })
          : Promise.resolve(null);
      const gpsFetch = Fetch("/trip-mapping/fetchMappedTripsFromMongo", {
        method: "post",
        data: { side: "mapped", tripId }
      });

      const [roadData, gpsData] = await Promise.all([roadFetch, gpsFetch]);
      const isMapped = (gpsData && gpsData.isMapped) || false;
      if (!isMapped) {
        message.warning("Trip not mapped");
      }
      this.setState({
        mappedRoute: route,
        mappedRoadRoute:
          roadData && roadData.length > 0
            ? roadData.map(p => [p.lat, p.lng])
            : [],
        mappedGpsPoints:
          gpsData && gpsData.gpsPoints && gpsData.gpsPoints.length > 0
            ? gpsData.gpsPoints
            : [],
        isMapped,
        mappedLoading: false
      });
    }
  }

  async handleMapTrip() {
    const {
      primaryServiceId,
      isMapped,
      primaryTripId,
      mappedTripId,
      primaryDetail
    } = this.state;
    if (!primaryTripId || !mappedTripId) {
      alert("Please select both trips before mapping.");
      return;
    }
    if (!primaryServiceId || !isMapped) {
      alert(
        "Could not resolve service or mapped trip. Please re-select the trips."
      );
      return;
    }
    const result = await Fetch("/trip-mapping/updateService", {
      method: "post",
      data: {
        tripId: primaryTripId,
        serviceId: primaryServiceId,
        mappedTripId: mappedTripId,
        serviceName: primaryDetail ? primaryDetail.service : null
      }
    });
    if (result) {
      alert("Trip mapped successfully.");
    }
  }

  render() {
    const {
      primaryDate,
      primaryStatus,
      primarySearchMode,
      primaryTripIdInput,
      primaryBusRc,
      primarySearched,
      primaryLoading,
      primaryTrips,
      primaryTripId,
      primaryDetail,
      primaryRoute,
      primaryRoadRoute,
      primaryGpsPoints,
      primaryIsMapped,
      primaryMappedTripId,
      mappedDate,
      mappedStatus,
      mappedSearchMode,
      mappedTripIdInput,
      mappedBusRc,
      mappedSearched,
      mappedLoading,
      mappedTrips,
      mappedTripId,
      mappedDetail,
      mappedRoute,
      mappedRoadRoute,
      mappedGpsPoints,
      isMapped,
      fullscreenMap
    } = this.state;

    return (
      <div className="rm-container">
        <div className="rm-columns">
          <div className="rm-column">
            <TripPanel
              type="primary"
              number="1"
              title="Primary Trip (New Trip)"
              subtitle="Select the original trip"
              date={primaryDate}
              minDate={today()}
              status={primaryStatus}
              searched={primarySearched}
              trips={primaryTrips}
              selectedTripId={primaryTripId}
              tripDetail={primaryDetail}
              onDateChange={v => this.setState({ primaryDate: v })}
              onStatusChange={v => this.setState({ primaryStatus: v })}
              onSearch={() => this.handleSearch("primary")}
              onTripSelect={v => this.handleTripSelect("primary", v)}
              searchMode={primarySearchMode}
              onSearchModeChange={v =>
                this.setState({
                  primarySearchMode: v,
                  primarySearched: false,
                  primaryTrips: [],
                  primaryTripId: "",
                  primaryRoute: [],
                  primaryRoadRoute: [],
                  primaryGpsPoints: [],
                  primaryIsMapped: false,
                  primaryMappedTripId: ""
                })
              }
              tripIdInput={primaryTripIdInput}
              onTripIdInputChange={v =>
                this.setState({ primaryTripIdInput: v })
              }
              busRc={primaryBusRc}
              onBusRcChange={v => this.setState({ primaryBusRc: v })}
              isMapped={primaryIsMapped}
              mappedExistingTripId={primaryMappedTripId}
            />
            {primaryLoading && (
              <div className="rm-map-placeholder">Loading map data…</div>
            )}
            {!primaryLoading && primaryRoute.length > 0 && (
              <TripMap
                label="Primary Trip"
                color="#1a7a6e"
                route={primaryRoute}
                roadRoute={primaryRoadRoute}
                gpsPoints={primaryGpsPoints}
                isFullscreen={fullscreenMap === "primary"}
                onToggleFullscreen={() =>
                  this.setState({
                    fullscreenMap:
                      fullscreenMap === "primary" ? null : "primary"
                  })
                }
              />
            )}
          </div>

          <div className="rm-column">
            <TripPanel
              type="mapped"
              number="2"
              title="Mapped Trip (Old Trip)"
              subtitle="Select the trip to map to"
              date={mappedDate}
              maxDate={today()}
              status={mappedStatus}
              searched={mappedSearched}
              trips={mappedTrips}
              selectedTripId={mappedTripId}
              tripDetail={mappedDetail}
              onDateChange={v => this.setState({ mappedDate: v })}
              onStatusChange={v => this.setState({ mappedStatus: v })}
              onSearch={() => this.handleSearch("mapped")}
              onTripSelect={v => this.handleTripSelect("mapped", v)}
              searchMode={mappedSearchMode}
              onSearchModeChange={v =>
                this.setState({
                  mappedSearchMode: v,
                  mappedSearched: false,
                  mappedTrips: [],
                  mappedTripId: "",
                  mappedRoute: [],
                  mappedRoadRoute: [],
                  mappedGpsPoints: [],
                  isMapped: false
                })
              }
              tripIdInput={mappedTripIdInput}
              onTripIdInputChange={v => this.setState({ mappedTripIdInput: v })}
              busRc={mappedBusRc}
              onBusRcChange={v => this.setState({ mappedBusRc: v })}
              isMapped={isMapped}
            />
            {mappedLoading && (
              <div className="rm-map-placeholder">Loading map data…</div>
            )}
            {!mappedLoading && mappedRoute.length > 0 && (
              <TripMap
                label="Mapped Trip"
                color="#f97316"
                route={mappedRoute}
                roadRoute={mappedRoadRoute}
                gpsPoints={mappedGpsPoints}
                isFullscreen={fullscreenMap === "mapped"}
                onToggleFullscreen={() =>
                  this.setState({
                    fullscreenMap: fullscreenMap === "mapped" ? null : "mapped"
                  })
                }
              />
            )}
          </div>
        </div>

        {isMapped && (
          <div className="rm-map-btn-wrap">
            <button className="rm-map-btn" onClick={() => this.handleMapTrip()}>
              ✓ MAP TRIP
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default RouteMapping;
